import type { ColumnDef } from '@tanstack/react-table';

import {
    OdooCompany,
    SupplierState,
    type GetPaginatedFilteredSuppliersQuery,
    type SuppliersFilterInput,
} from 'data/__generated__';
import { multiSelectColumn, textColumn } from 'components/DataTable';
import { getOdooPartnerUrl } from 'helpers/odoo';

import { StatusTag } from '../components/StatusTag';
import { ODOO_COMPANY_LABELS } from './labels';
import { SUPPLIER_STATES_LABELS } from './states';

export type SupplierRow = GetPaginatedFilteredSuppliersQuery['paginatedFilteredSuppliers']['suppliers'][number];

const SUPPLIER_STATES = Object.values(SupplierState);
const ODOO_COMPANIES = Object.values(OdooCompany);

export const suppliersColumns: ColumnDef<SupplierRow, unknown>[] = [
    textColumn<SupplierRow, SuppliersFilterInput>({
        accessorKey: 'name',
        header: 'Nom',
        defaultVisible: true,
    }),
    textColumn<SupplierRow, SuppliersFilterInput>({
        accessorKey: '_id',
        header: 'ID',
        defaultVisible: true,
    }),
    multiSelectColumn<SupplierRow, SuppliersFilterInput>({
        accessorKey: 'odooCompany',
        header: 'Entité juridique acheteuse',
        filterKey: 'odooCompany',
        defaultVisible: true,
        options: ODOO_COMPANIES.map((odooCompany) => ({
            value: odooCompany,
            label: ODOO_COMPANY_LABELS[odooCompany],
        })),
        cell: ({ row }) => ODOO_COMPANY_LABELS[row.original.odooCompany],
    }),
    multiSelectColumn<SupplierRow, SuppliersFilterInput>({
        accessorKey: 'state',
        header: 'Statut',
        filterKey: 'state',
        defaultVisible: true,
        options: SUPPLIER_STATES.map((state) => ({ value: state, label: SUPPLIER_STATES_LABELS[state] })),
        renderOption: ({ value, label }) => {
            const state = SUPPLIER_STATES.find((supplierState) => supplierState === value);
            return state ? <StatusTag state={state} /> : label;
        },
        cell: ({ row }) => <StatusTag state={row.original.state} />,
    }),
    textColumn<SupplierRow, SuppliersFilterInput>({
        accessorKey: 'odooSupplierUrl',
        header: 'Lien vers Odoo',
        defaultVisible: true,
        cell: ({ row }) => {
            const { odooSupplierId, odooSupplierUrl } = row.original;

            if (!odooSupplierId) {
                return '—';
            }

            return (
                <a
                    href={odooSupplierUrl ?? getOdooPartnerUrl(odooSupplierId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline"
                    data-test="supplier-odoo-link"
                >
                    Fournisseur #{odooSupplierId}
                </a>
            );
        },
    }),
];
